import axios from 'axios'

import { CompanyInterface, CompanyPlatformInterface, LogoURLInterface } from '../Interfaces/CompanyInterface'
import { CompanySearchResultInterface } from '../Interfaces/CompanySearchResultInterface'
import { Immutable } from '../Other/Immutable'
import { updateUserAttributes } from './cognito'
import { getRequestConfig } from './index'

export async function getCompanyLogoUrl(tradenameId: string): Promise<string | null> {
  if (tradenameId === undefined) {
    throw Error('tradename id cannot be undefined')
  }
  // AWS S3 bucket pre-signed urls does not support the Authentication header so we cannot use the HTTP 302
  // redirect directly via platform in the browser as the browser will send the Authentication header to the
  // redirected url. :(
  //
  // See https://github.com/axios/axios/issues/2855
  //
  // So instead we fetch the S3 pre-signed url for the logo without the redirect, and then the browser
  // can request the logo directly from the bucket with the actual S3 pre-signed url.
  try {
    const platformUrl = `/platform/v4/tradenames/${tradenameId}/logo?noRedirect=true`
    const { data } = await axios.get(platformUrl, getRequestConfig())
    return data.url
  } catch (error) {
    console.error('No logo found.')
    return null
  }
}

export const asCoreCompany = (pCompany: CompanyPlatformInterface): CompanyInterface => {
  const {
    language,
    name,
    currencyId,
    country,
    email,
    globalId,
    id,
    vatInformation,
    registrationNumber,
    contactInformation,
    randomBucket,
    agerasNextMigration,
    userId,
    insertTime,
    externalOrganisationId,
    uiSetup,
  } = pCompany
  const vatNumber = (vatInformation && vatInformation.number) || ''
  const vatPct = (vatInformation && vatInformation.vatPct) || null
  const phoneNumber = (contactInformation && contactInformation.phoneNumber) || ''
  const firstName = (contactInformation && contactInformation.firstName) || ''
  const lastName = (contactInformation && contactInformation.lastName) || ''
  const vatNumberValidation = (vatInformation && vatInformation.verification) || ''
  return {
    language,
    name,
    currencyId,
    country,
    email,
    globalId,
    id,
    vatNumber,
    businessId: registrationNumber || '',
    vatNumberValidation,
    agerasNextMigration,
    externalOrganisationId,
    phoneNumber,
    firstName,
    lastName,
    ...(vatPct !== null ? { vatPct } : {}),
    randomBucket,
    userId,
    insertTime,
    uiSetup,
  }
}

export async function uploadImageData(
  dataFile: Blob,
  fileName: string,
  tradeNameId: string,
): Promise<LogoURLInterface> {
  const data = new FormData()
  data.append('file', dataFile, fileName)
  const requestConfig = {
    headers: {
      ...getRequestConfig().headers,
      ...{ 'Content-Type': 'multipart/form-data;' },
    },
  }
  const url = `/platform/v4/logo?tradenameId=${tradeNameId}`
  const response = await axios.post(url, data, requestConfig)
  return response.data as LogoURLInterface
}

export async function deleteCompanyLogo(tradenameId: string) {
  await axios.delete(`/platform/v4/tradenames/${tradenameId}/logo`, getRequestConfig())
}

export async function getCompany(): Promise<CompanyInterface> {
  const res = await axios.get('/platform/v4/company', getRequestConfig())
  const company = asCoreCompany(res.data)
  return company
}

export async function getCompanySettings() {
  const response = await axios.get('/core/rest/settings', getRequestConfig())
  return response.data.data
}

export const asPlatformCompany = (origCompany) => {
  const company = Immutable.copy(origCompany)
  const { vatNumber, businessId, vatPct } = company

  company.vatInformation = {
    ...(vatNumber ? { number: vatNumber } : {}),
    ...(vatPct ? { vatPct } : {}),
  }
  if (businessId !== undefined) {
    company.registrationNumber = businessId
  }
  delete company.vatNumber
  delete company.vatPct
  delete company.businessId
  return company
}

export async function updateCompanyVatNumber(vatNumber: string): Promise<CompanyPlatformInterface> {
  const company = { vatInformation: { number: vatNumber } }
  const res = await axios.patch('/platform/v4/company', company, getRequestConfig())
  return asCoreCompany(res.data)
}

export async function validateCompanyVatNumber(vatNumber: string): Promise<CompanyPlatformInterface> {
  const company = { vatInformation: { number: vatNumber } }
  const res = await axios.patch('/platform/v4/company', company, getRequestConfig())
  return res.data
}

export async function updateCompany(origCompany: CompanyInterface): Promise<CompanyPlatformInterface> {
  // ZERV-1933 only update if language is the one changing
  if (origCompany.language) {
    await updateUserAttributes({
      'custom:language': origCompany.language,
    })
  }
  const company = asPlatformCompany(origCompany)
  const res = await axios.patch('/platform/v4/company', company, getRequestConfig())
  return asCoreCompany(res.data)
}

export async function searchCompanies(country: string, search: string): Promise<CompanySearchResultInterface[]> {
  const response = await axios.get(`/platform/v4/companies/${country}/byName/${search}`, getRequestConfig())
  return response.data
}

window.getCompany = getCompany()
